import { useEffect } from 'react';
import { getServices } from '@breath-lab/services';
import { AnalyticsEvent, AnalyticsParam, dev } from '@breath-lab/domain';
import { useAuth } from './useAuth';

// Small negative tolerance absorbs device clock skew; anything outside this
// window means the observed trialStartedAt was already live before this
// session started (stale — already seen) or the clock is off by more than a
// few minutes. Either way, never fire for it.
const FRESHNESS_MIN_MS = -5 * 60 * 1000;
const FRESHNESS_MAX_MS = 10 * 60 * 1000;

function firedFlagKey(uid: string, trialStartedAtMs: number): string {
  return `trial_started_fired:${uid}:${trialStartedAtMs}`;
}

/**
 * Fires AnalyticsEvent.trialStarted ({ trial_type: 'auto_grant' }) exactly
 * once per user the first time a fresh `trialStartedAt` is observed on the
 * signed-in user — i.e. when the server-side no-card trial grant
 * (functions/src/v2/hooks/auth.ts) lands on the user doc. Deliberately kept
 * separate from useTrialState, which stays pure and only feeds root
 * analytics user properties; this hook owns the one-time side effect and its
 * own dedupe bookkeeping (keyed by uid + trialStartedAt so an account switch
 * on a shared device never suppresses a different user's legitimate event).
 */
export function useTrialStartedEvent(): void {
  const uid = useAuth((state) => state.user?.uid ?? null);
  const trialStartedAtMs = useAuth((state) => state.user?.trialStartedAt?.getTime() ?? null);

  useEffect(() => {
    if (!uid || trialStartedAtMs == null) return;

    const deltaMs = Date.now() - trialStartedAtMs;
    if (deltaMs <= FRESHNESS_MIN_MS || deltaMs >= FRESHNESS_MAX_MS) return;

    const storage = getServices().storage;
    const key = firedFlagKey(uid, trialStartedAtMs);

    // A read failure is treated as "not yet fired" — worst case is one
    // duplicate event, never a crash or a silently dropped first event.
    try {
      if (storage.get(key)) return;
    } catch (err) {
      dev.warn('[useTrialStartedEvent] storage read failed', err);
    }

    void getServices().analytics.logEvent(AnalyticsEvent.trialStarted, {
      [AnalyticsParam.trialType]: 'auto_grant',
    });

    try {
      storage.set(key, '1');
    } catch (err) {
      dev.warn('[useTrialStartedEvent] storage write failed', err);
    }
  }, [uid, trialStartedAtMs]);
}
